import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react'
import debounce from 'lodash/debounce'
import { getAirportDisplayText } from 'lib/flights'
import { getAirportsSearch } from 'api/flights'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidget'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { connect } from 'react-redux'
import { PopularAirports } from 'components/Flights/types'
import { filterAirportRecords } from 'lib/flights'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import AirportSelectOptions from './AirportSelectOptions'
import FlightSearchMenu from '../FlightSearchMenu/FlightSearchMenu'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import ModalContext from 'contexts/ModalContext'

interface Props {
  title: string;
  isDeparture?: boolean;
  className?: string;
  popularAirports: PopularAirports;
}

function FlightSearchAirportSelectMobile(props: Props) {
  const {
    title,
    isDeparture,
    className,
    popularAirports,
  } = props

  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const { departureAirport, arrivalAirport, activeMenu } = state
  const airport = isDeparture ? departureAirport : arrivalAirport
  const [inputValue, setInputValue] = useState(airport ? getAirportDisplayText(airport) : '')
  const [airportLocations, setAirportLocations] = useState<Array<App.AirportLocation>>([])
  const [locationsFetching, setLocationsFetching] = useState(false)
  const currentMenu = isDeparture ? StandaloneFlightMenu.departAirport : StandaloneFlightMenu.arrivalAirport
  const actionType = isDeparture ? FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT : FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT
  const heading = inputValue ? undefined : 'Popular airports'
  const popularAirportsList = isDeparture ? popularAirports.origins : popularAirports.destinations

  const setActiveMenu = useCallback((activeMenu?: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  const filteredAirports = useMemo(() => filterAirportRecords(
    airportLocations,
    isDeparture ? arrivalAirport : departureAirport,
  ), [airportLocations, isDeparture, departureAirport, arrivalAirport])

  const debouncedLoadAirports = useMemo(() => debounce((value: string) => {
    getAirportsSearch(value).then(airports => {
      setLocationsFetching(false)
      setAirportLocations(airports)
    })
  }, 500), [setAirportLocations])

  const onChangeInputText = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setInputValue(value)
    const searchText = value.trim()

    if (searchText.length > 2) {
      setLocationsFetching(true)
      debouncedLoadAirports(searchText)
    }
    if (activeMenu !== currentMenu) {
      setActiveMenu(currentMenu)
    }

    if (value.length === 0) {
      dispatch({ type: actionType, airport: null })
    }
  }, [activeMenu, currentMenu, debouncedLoadAirports, setActiveMenu, dispatch, setInputValue, actionType])

  const onSelectAirport = useCallback((airport: App.AirportLocation) => {
    setActiveMenu(isDeparture ? undefined : StandaloneFlightMenu.date)
    setInputValue(getAirportDisplayText(airport))
    dispatch({ type: actionType, airport })
    setAirportLocations(popularAirportsList)
  }, [isDeparture, actionType, popularAirportsList, setActiveMenu, setInputValue, dispatch, setAirportLocations])

  useEffect(() => {
    if (!inputValue.length && popularAirportsList?.length > 0) {
      setAirportLocations(popularAirportsList)
    }
  }, [inputValue, popularAirportsList])

  const showModal = useContext(ModalContext)

  const onShowAirportModal = useCallback(async() => {
    await showModal(
      <FlightSearchMenu title={isDeparture ? 'From' : 'To'}>
        <AirportSelectOptions
          loading={locationsFetching}
          onSelectAirport={onSelectAirport}
          onChangeText={onChangeInputText}
          inputValue={inputValue}
          airports={filteredAirports}
          heading={heading}
        />
      </FlightSearchMenu>,
    )
    setActiveMenu()
  }, [filteredAirports, heading, inputValue, isDeparture, locationsFetching, onChangeInputText, onSelectAirport, setActiveMenu, showModal])

  return (<SearchFormField
    className={className}
    value={inputValue}
    label={title}
    required
    placeholder="Search by city or airport"
    onClick={onShowAirportModal}
    requiredErrorMessage={isDeparture ? 'Please enter a departure' : 'Please enter a destination'}
  />
  )
}

function mapStateToProps(state: App.State) {
  return {
    popularAirports: state.flights.popularAirports,
  }
}

export default connect(mapStateToProps)(FlightSearchAirportSelectMobile)
